import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import LockIcon from "@mui/icons-material/Lock";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";

import type { AuthErrorReason } from "../../services/auth";

interface AuthErrorScreenProps {
  reason: AuthErrorReason;
  /** Starts a new sign-in; only ever on the user's click, so this screen cannot loop. */
  onRetry: () => void;
}

/**
 * Shown instead of the app when signing in failed, or when signing in again did not help within the loop-guard
 * window (services/auth signInAgain). Rendered outside the app's providers, so it uses plain MUI only.
 */
const AuthErrorScreen = ({ reason, onRetry }: AuthErrorScreenProps): JSX.Element => {
  const { t } = useTranslation();

  return (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", padding: "2rem" }}>
      <Paper variant="outlined" sx={{ padding: "3rem 2rem", textAlign: "center", maxWidth: "600px" }}>
        <LockIcon sx={{ fontSize: "4rem", opacity: 0.7, marginBottom: "1.5rem" }} color="error" />
        <Typography variant="h5" component="h1" sx={{ marginBottom: "1rem", fontWeight: 600 }}>
          {t("auth_error.title")}
        </Typography>
        <Typography sx={{ marginBottom: "2rem", lineHeight: 1.6 }}>{t("auth_error.message")}</Typography>
        <Button variant="contained" onClick={onRetry}>
          {t("auth_error.retry")}
        </Button>
        <Typography variant="caption" component="p" sx={{ marginTop: "1.5rem", opacity: 0.6 }}>
          {t("auth_error.code", { code: reason })}
        </Typography>
      </Paper>
    </Box>
  );
};

export default AuthErrorScreen;
